import { Injectable } from '@angular/core';
import { DatePipe } from '@angular/common';
import { CurrencyPipe } from "@angular/common";
import { TitleCasePipe } from "@angular/common";
import { DateService } from './date.service';
import { ColDef, GridOptions, ValueFormatterParams, GridApi, ExcelExportParams, ExcelStyle, ISetFilterParams, SelectionColumnDef } from "ag-grid-community";
import { GISPointTypes } from "../../common/models/well.model";
import { dateFilterComparator } from '../components/data-table/comparators/data-table.comparators';
import { IBooleanFilterParams } from '../components/ag-grid/filters/boolean-filter.component';
import { ButtonRendererComponent } from '../components/ag-grid/button-renderer.component';
import { CheckboxRendererComponent } from '../components/ag-grid/checkbox-renderer.component';
import { SwitchRendererComponent } from '../components/ag-grid/switch-renderer.component';
import { ActionCellRendererComponent } from '../components/ag-grid/action-cell-renderer.component';
import { EditableGroupRowRendererComponent } from '../components/ag-grid/editable-group-row-renderer.component';
import { ModalInfoComponent } from '../components/ag-grid/modal-info.component';
import { SelectionColumnHeaderComponent } from '../components/ag-grid/selection-column-header.component';
import { RouterLinkRendererComponent } from '../components/ag-grid/router-link-renderer.component';
import * as jp from 'jsonpath';

const INTERACTIVE_CELL_RENDERERS = [
    ButtonRendererComponent,
    CheckboxRendererComponent,
    SwitchRendererComponent,
    ActionCellRendererComponent,
    EditableGroupRowRendererComponent,
    ModalInfoComponent,
    RouterLinkRendererComponent,
];


@Injectable({
    providedIn: 'root'
})
export class GridFormatterService {
    defaultSelectionColumnDef: SelectionColumnDef = {
        headerComponent: SelectionColumnHeaderComponent,
        headerClass: "wrapped",
        suppressAutoSize: true,
        suppressSizeToFit: true,
        maxWidth: 75,
        minWidth: 75,
        filter: null,
        sortable: false
    } as SelectionColumnDef;

	defaultTextFilterParams = { debounceMs: 500, buttons: ['reset'] } as any;// as ITextFilterParams;
	defaultNumberFilterParams = { debounceMs: 500, buttons: ['reset'] } as any;// as INumberFilterParams;
	defaultDateFilterParams = { comparator: dateFilterComparator, buttons: ['reset'], browserDatePicker: true, includeTime: false, } as any;// as IDateFilterParams;
    defaultSetFilterParams = { suppressMiniFilter: true } as ISetFilterParams;
    defaultBooleanFilterParams = {falseValueDisplayFormat: 'No', trueValueDisplayFormat: 'Yes', isInServerSideFilterMode: true, } as IBooleanFilterParams;
    defaultColDef: ColDef = {
        headerClass: "wrapped",
        filter: true,
        resizable: true,
        sortable: true,
        filterParams: this.defaultSetFilterParams,
        suppressKeyboardEvent: (params: any) => {
            const key = params.event.key;
            if (key !== ' ' && key !== 'Enter') return false;
            return INTERACTIVE_CELL_RENDERERS.some(r => params.colDef.cellRenderer === r);
        }
    };
    defaultGridOptions = <GridOptions>{
        defaultColDef: this.defaultColDef,
        // Use ag-grid's own tooltip component instead of the native `title` attribute so header
        // tooltips (see applyFilterKeyboardHintTooltips) can appear on keyboard focus, not just mouse hover.
        enableBrowserTooltips: false,
        tooltipTrigger: 'focus',
        pagination: true,
        paginationAutoPageSize: true,
        suppressHorizontalScroll: false,
        suppressCutToClipboard: true,
        headerHeight: 50
    };
    defaultGridOptionsServerSide = <GridOptions>{
        cacheOverflowSize: 2,
        cacheBlockSize: 100,
        maxConcurrentDatasourceRequests: 1,
        maxBlocksInCache: 10,
        paginationPageSize: 100,
        rowBuffer: 0,
        rowSelection: "multiple",
        rowModelType: "serverSide",
        suppressHorizontalScroll: false,
        defaultColDef: this.defaultColDef,
        enableBrowserTooltips: false,
        tooltipTrigger: 'focus',
        pagination: true,
        suppressCutToClipboard: true,
        headerHeight: 50
    };

    excelStyles: ExcelStyle[] = [
        {
            id: 'booleanType',
            dataType: 'Boolean',
        },
        {
            id: 'stringType',
            dataType: 'String',
        },        
        {
            id: 'dateType',
            dataType: 'DateTime',
            numberFormat: {
                format: 'MM/dd/yyyy'
            }
        },
        {
            id: 'dateTimeType',
            dataType: 'DateTime',
            numberFormat: {
                format: 'MM/dd/yyyy hh:mm:ss AM/PM'
            }
        }
    ];


    constructor(
        private datePipe: DatePipe,
		private currencyPipe: CurrencyPipe,
		private titleCasePipe: TitleCasePipe
    ) { }

    public BuildDefaultGridOptionsWithColumns(columns: ColDef[], isServerSide: boolean = false, canFilter: boolean = true, canSort: boolean = true, autoSizeColumns: boolean = false): GridOptions {
        const base = isServerSide ? this.defaultGridOptionsServerSide : this.defaultGridOptions;
        let gridOptions: GridOptions = { ...base, defaultColDef: { ...this.defaultColDef } };
        if (gridOptions.defaultColDef == null) {
            gridOptions.defaultColDef = {};
        }
        gridOptions.defaultColDef.filter = canFilter;
        gridOptions.defaultColDef.sortable = canSort;
        if (canFilter) {
            this.applyFilterKeyboardHintTooltips(columns);
        }
        gridOptions.columnDefs = columns;
        gridOptions.editType = 'fullRow';
        gridOptions.headerHeight = 50;
        if (autoSizeColumns) {
            gridOptions.onGridReady = (params) => {
                params.api.sizeColumnsToFit();
                params.api.autoSizeAllColumns();
            }
        }
        else {
            gridOptions.onGridReady = (params) => {
                params.api.sizeColumnsToFit();
            }

        }
        return gridOptions;
    }

    // Surfaces the keyboard shortcut for opening a column's filter, since the header's filter/menu
    // icons are aria-hidden and not independently focusable (ag-grid's native Tab handling doesn't
    // reach them - see notifications-widget/organization-wells-grid history for why a DOM patch
    // wasn't viable). Callable directly by grids that build GridOptions by hand instead of via
    // BuildDefaultGridOptionsWithColumns.
    public applyFilterKeyboardHintTooltips(columns: ColDef[]): void {
        columns.forEach(c => {
            if (c.filter === false || !c.headerName) return;
            c.headerTooltip = `${c.headerName} column: press Ctrl+Enter to filter, Alt+Down for column menu`;
        });
    }


    public formatDate(field: string, format: string = DateService.DateFormat, defaultText: string = "N/A", timeZone: string = ""): any {
        return (obj: any) => {
            let value: string | null = defaultText;
            let fieldValue = this.tryGetValue(obj.data, field);
			if (!this.isNullUndefinedOrEmpty(fieldValue) && fieldValue !== "{}") {
				if (timeZone != null && timeZone != "") {
					value = this.datePipe.transform(fieldValue, format, timeZone);
				} else {
					value = this.datePipe.transform(fieldValue, format);
				}
				
            }
            return value;
        }
    }   

    public formatNumberPrecision(field: string, precision: number, defaultText: string = ""): any {
		return (obj: any) => {
			
            let fieldValue = this.tryGetValue(obj.data, field);
			if (!this.isNullUndefinedOrEmpty(fieldValue)) {
				let value = Number(fieldValue).toFixed(precision);
				return parseFloat(value); //parsing it back will remove any trailing zeros and leave the result a NUMBER, not a string
				
			}
            else return defaultText;
        }
    }

    //as of ag-grid 35, this function no longer works correctly as ag-grid is overriding the valueFormatter definition with its internal logic to render a checkbox
    //Use the CheckBoxRendererComponent as cellRenderer, with a cellRendererParam of "renderAsYesNo" = true instead.
    public formatBooleanToYesNo(field: string, defaultValue: string = "N/A"): any {
		return (obj: any) => {
            if (obj.data) {
                let fieldValue = this.tryGetValue(obj.data, field);
				if (fieldValue != null) {
					return fieldValue ? 'Yes' : 'No';
				}                            
			}
			return defaultValue;
        }
    }

    public formatSourceBoolean(field: string): any {
        return (obj: any) => {
            if (obj.data) {
                let fieldValue = this.tryGetValue(obj.data, field);

                if (fieldValue != undefined && fieldValue != null)
                    return fieldValue ? 'Paper' : 'RICS';

                else return "RICS";
            }
        }
	}

	public formatWellCoordinateValue(pointType: GISPointTypes, field: string, defaultValue: string = "N/A"): any {
		return (obj: any) => {
			let value = defaultValue;
			if (obj != null && obj.data != null && obj.data['location'] != null && obj.data['location']['gisPoints'] != null) {
				let point = obj.data['location']['gisPoints'].find(g => g['pointType'] == pointType);
				if (point != null && point['coordinates'] != null && point['coordinates'][field] != null) {
					value = point['coordinates'][field];
				}
			}
			return value;
		}
	}

	public formatWellName(defaultValue: string = "N/A"): any {
		return (obj: any) => {
			let value: string = defaultValue;
			if (obj != null && obj.data != null) {
				if (obj.data["lease"] != null && obj.data["lease"]["name"] != null && obj.data["lease"]["name"].length > 0) {
					value = obj.data["lease"]["name"];
				}
				if (value == defaultValue) {
					if (obj.data["name"] != null) {
						value = obj.data["name"];
						if (obj.data["wellNumber"] != null && obj.data["wellNumber"].length > 0) {
							let wellNumber = obj.data["wellNumber"];
							if (value.endsWith(wellNumber)) {
								value = value.substring(0, value.length - wellNumber.length).trim();
							}
						}
					}
				}
			}
			return value;
		}
	}

    public gridReadySizeToFit(params: any) {
        window.addEventListener('resize', () => {
            setTimeout(() => {
                params.api.sizeColumnsToFit();
            });
        });
        params.api.sizeColumnsToFit();
	}

	public gridReadyAutoSizeColumns(params: any) {
		window.addEventListener('resize', () => {
			setTimeout(() => {
				params.api.autoSizeAllColumns();
			}, 1000);
		});
		params.api.autoSizeAllColumns();
	}

	public arraySelectorFormatter(field: string, options: (array: any) => any, index: number | null = null, defaultValue: string = "N/A", titleCaseResult: boolean = false): any {
		return (obj: any) => {
			let result = defaultValue;
			if (options) {
				if (obj.data) {
					let fieldValue = this.tryGetValue(obj.data, field);
					if (fieldValue != null) {
						if (index == null) {
							result = options(fieldValue as any);
						} else {
							result = options(fieldValue[index] as any);
						}						
						if (titleCaseResult) {
							result = this.titleCasePipe.transform(result);
						}
					}
				}				
			}
			return result;
		}
	}

    public exportDataToExcel(fileName: string, sheetName: string, api?: GridApi, selectedOnly: boolean = false): void {
        if (api == null) return;
        fileName = fileName.replace('.', '');
        const params = <ExcelExportParams>{
            skipHeader: false,
            columnKeys: api.getAllGridColumns() ?? undefined,
            fileName: fileName,
            sheetName: sheetName,
            onlySelected: selectedOnly,
            onlySelectedAllPages: selectedOnly,
            processCellCallback: (params) => this.processCallback(params)
        };

        api.exportDataAsExcel(params);
    }

    public flagEnumFormatter(field: string, enumObject: any, selectedFlagEnumValue: string, formatAsBool: boolean = false): any {
        return (obj: any) => {
            var yesNoResult = "No" as any;

            if (enumObject && obj.data && selectedFlagEnumValue != null && selectedFlagEnumValue != undefined) {
                let fieldValue = this.tryGetValue(obj.data, field);

                if (fieldValue != undefined && fieldValue != null) {
                    if (!formatAsBool)
                        yesNoResult = (fieldValue & enumObject[selectedFlagEnumValue]) > 0 ? "Yes" : "No";
                    else
                        yesNoResult = (fieldValue & enumObject[selectedFlagEnumValue]) > 0;
                }
            }

            return yesNoResult;
        }
    }

    public formatPotentiallyBlankField(field: string, valueWhenBlank: string) {
		return (obj: any) => {
			return (obj.data && obj.data[field] && obj.data[field].length > 0) ? obj.data[field] : valueWhenBlank
		};
    }

    public formatCurrency(field: string, currency: string = 'USD', display: string = 'symbol', digits = '1.2-2'): any {
        return (obj: any) => {
            if (obj.data) {
                let fieldValue = this.tryGetValue(obj.data, field);
                if (fieldValue != null) return this.currencyPipe.transform(fieldValue, currency, display, digits);
            }
            return this.currencyPipe.transform(0, currency, display, digits);
        }
	}

	public formatTitleCase(field: string): any {
		return (obj: any) => {
			if (obj.data) {
				let fieldValue = this.tryGetValue(obj.data, field);
				if (fieldValue != null) {
					return this.titleCasePipe.transform(fieldValue);
				}
				return fieldValue;
			}
			return "";
		}
	}

	public titleCase(value: string): any {
		return this.titleCasePipe.transform(value);
	}

    public tryGetValue(data: any, path: string): any {
        let value = "";
        try {
            if (data != null) {
                //Prefix dollar sign operator if doesn't exist
                if (!path.startsWith("$."))
                    path = "$." + path;
                value = jp.value(data, path);
            }           
        } catch (error) {
            console.error(`Error in path: ${path}`);
            console.error(error);
        }
        return value;
    }

    public isNullUndefinedOrEmpty(data: any) : boolean {
        return data === null || data === undefined || data === "";
    }

    private processCallback(params: any): string {
        const colDef: ColDef = params.column.getColDef();
        let returnValue = params.value;

		if (colDef.cellClass == null && colDef.valueFormatter != null) {
			const valueFormatterParams: ValueFormatterParams = {
				...params,
				data: params.node.data,
				node: params.node!,
				colDef: params.column.getColDef()
			};
			if (typeof colDef.valueFormatter === 'function') {
				returnValue = colDef.valueFormatter(valueFormatterParams);
			} else {
				returnValue = colDef.valueFormatter;
			}
		}
        return returnValue;
    }

}
